import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from './Logo';
import './Navbar.css';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 100;
      const elementPosition = element.offsetTop;
      const offsetPosition = elementPosition - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleNavigation = (path: string, sectionId?: string) => {
    closeMenu();

    if (path === '/' && location.pathname === '/') {
      // Already on home page, just scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (path === '/products') {
      // Navigate to home and scroll to products section
      if (location.pathname === '/' || location.pathname === '/products') {
        // Already on the page, just scroll
        scrollToSection('products');
      } else {
        // Navigate to home first, then scroll
        navigate('/');
        setTimeout(() => scrollToSection('products'), 100);
      }
    } else {
      // Regular navigation for other pages
      navigate(path);
    }
  };

  // Track active section on scroll (only on home page)
  useEffect(() => {
    if (location.pathname !== '/' && location.pathname !== '/products') {
      setActiveSection('');
      return;
    }

    const handleScroll = () => {
      const homeSection = document.getElementById('home');
      const productsSection = document.getElementById('products');

      if (!homeSection || !productsSection) return;

      const scrollPosition = window.scrollY + 150; // Offset for navbar
      const productsStart = productsSection.offsetTop;

      if (scrollPosition < productsStart) {
        setActiveSection('home');
      } else {
        setActiveSection('products');
      }
    };

    handleScroll(); // Check initial position
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isMenuOpen && !target.closest('.nav-menu') && !target.closest('.nav-toggle')) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const isActive = (path: string) => {
    // On home page, check which section is active
    if (location.pathname === '/' || location.pathname === '/products') {
      if (path === '/') {
        return activeSection === 'home';
      }
      if (path === '/products') {
        return activeSection === 'products';
      }
    }
    // On other pages, check pathname match
    return location.pathname === path;
  };

  const menuItems = [
    { path: '/', label: 'Home', scrollTo: false },
    { path: '/products', label: 'Products', scrollTo: true },
    { path: '/industries', label: 'Industries', scrollTo: false },
    { path: '/about', label: 'About', scrollTo: false },
    { path: '/contact', label: 'Contact Us', scrollTo: false }
  ];

  return (
    <nav className="navbar">
      <div className="nav-container">
        <button
          onClick={() => handleNavigation('/')}
          className="nav-logo"
          aria-label="Go to home page"
        >
          <Logo size="large" showText={false} variant="dark" />
        </button>

        <div className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
          {menuItems.map((item) => (
            item.scrollTo || item.path === '/' ? (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                aria-label={`Go to ${item.label} page`}
              >
                {item.label}
              </button>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeMenu}
                className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                aria-label={`Go to ${item.label} page`}
              >
                {item.label}
              </Link>
            )
          ))}
        </div>

        <button
          className={`nav-toggle ${isMenuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle navigation menu"
          aria-expanded={isMenuOpen}
        >
          <span className="bar"></span>
          <span className="bar"></span>
          <span className="bar"></span>
        </button>
      </div>
    </nav>
  );
};

export default Navbar; 